import HiddenInput from "@/components/form/components/HiddenInput";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Modal, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateContractAttachment } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { ContractAttachmentType } from "../../enums";
import { ContractAttachment, UpdateContractAttachment } from "../../types";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  contractId: UUID;
  attachment?: ContractAttachment;
};

export const ContractAttachmentTypeModalForm = ({ isOpen, onCancel, contractId, attachment }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UpdateContractAttachment>();

  const updateContractAttachment = useUpdateContractAttachment();

  useEffect(() => {
    if (isOpen && attachment) {
      form.setFieldsValue({
        optimisticLockVersion: attachment.optimisticLockVersion,
        type: attachment.type,
      });
    }
  }, [attachment, form, isOpen]);

  const handleFormSubmit = () => {
    if (!attachment) {
      return;
    }

    form.validateFields().then((values) =>
      updateContractAttachment.mutate(
        { id1: contractId, id2: attachment.id, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => setErrorsToForm(form, "contract.attrs.attachment", error.violations),
        },
      ),
    );
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      title={t("contract.titles.updateAttachmentType")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateContractAttachment.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="contractAttachmentTypeModalForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item name="type" label={t("contract.enums.contractAttachmentType._label")} rules={[validations.notNull]}>
          <Select
            {...selectStandardProps}
            options={Object.values(ContractAttachmentType).map((type) => ({
              value: type,
              label: t(`contract.enums.contractAttachmentType.${type}`),
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
